import { COLORS, FONT_WEIGHT, TYPOGRAPHY } from '../styles/theme'

/**
 * Get initial nodes for a new project
 * Projects start with a blank canvas (no nodes)
 */
export function getInitialNodes() {
  return []
}

export const INITIAL_EDGES = [] as const

// Shared node styling constants (same for all categories)
export const NODE_SHARED_STYLES = {
  backgroundColor: '#ffffff',
  borderColor: '#e5e7eb',
  borderRadius: '4px',
  fontSize: TYPOGRAPHY.caption.fontSize,
  fontWeight: FONT_WEIGHT.regular,
  accentColor: COLORS.primary, // Primary accent for interactive elements
} as const

// Centralized text styles for consistent typography across the app
export const TEXT_STYLES = {
  // Secondary/helper text - used for subheadings, descriptions, hints
  secondary: {
    color: COLORS.textSecondary,
    fontSize: TYPOGRAPHY.caption.fontSize,
    lineHeight: '1.4',
  },
  // Smaller secondary text - used for node subheadings
  secondarySmall: {
    color: COLORS.textSecondary,
    fontSize: TYPOGRAPHY.caption.fontSize,
    lineHeight: '1.3',
  },
  // Label text - used for field labels, section headers
  label: {
    color: COLORS.textPrimary,
    fontSize: TYPOGRAPHY.caption.fontSize,
    fontWeight: FONT_WEIGHT.semibold,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
} as const

// Category-specific colors (only what differs per category)
const CATEGORY_COLORS = {
  information: {
    iconColor: '#6b7280', // Gray
    gradientColor: '#9ca3af',
  },
  idea: {
    iconColor: COLORS.primary, // App primary color
  },
  need: {
    iconColor: '#f59e0b', // Amber
  },
  summarized: {
    iconColor: '#64748b', // Slate
    gradientColor: '#94a3b8',
  },
  venture: {
    iconColor: '#3b8cf6', // Blue
  },
  storyboard: {
    iconColor: '#8b5cf6', // Violet for storyboard nodes
    gradientColor: '#a78bfa',
  },
  survey: {
    iconColor: '#3b82f6', // Blue (same as venture) — real data nodes
    gradientColor: '#3b82f6',
  },
  interview: {
    iconColor: '#3b82f6', // Blue (same as venture) — real data nodes
    gradientColor: '#3b82f6',
  },
  insight: {
    iconColor: '#3b82f6', // Blue (same as venture)
    gradientColor: '#3b82f6',
  },
} as const

// Full category styles (combines shared + category-specific)
// gradientColor defaults to iconColor when not specified
export const CATEGORY_STYLES = {
  information: {
    ...NODE_SHARED_STYLES,
    iconColor: CATEGORY_COLORS.information.iconColor,
    gradientColor: CATEGORY_COLORS.information.gradientColor,
  },
  idea: {
    ...NODE_SHARED_STYLES,
    iconColor: CATEGORY_COLORS.idea.iconColor,
    gradientColor: CATEGORY_COLORS.idea.iconColor,
  },
  need: {
    ...NODE_SHARED_STYLES,
    iconColor: CATEGORY_COLORS.need.iconColor,
    gradientColor: CATEGORY_COLORS.need.iconColor,
  },
  summarized: {
    ...NODE_SHARED_STYLES,
    iconColor: CATEGORY_COLORS.summarized.iconColor,
    gradientColor: CATEGORY_COLORS.summarized.gradientColor,
  },
  venture: {
    ...NODE_SHARED_STYLES,
    iconColor: CATEGORY_COLORS.venture.iconColor,
    gradientColor: CATEGORY_COLORS.venture.iconColor,
  },
  storyboard: {
    ...NODE_SHARED_STYLES,
    iconColor: CATEGORY_COLORS.storyboard.iconColor,
    gradientColor: CATEGORY_COLORS.storyboard.gradientColor,
  },
  survey: {
    ...NODE_SHARED_STYLES,
    iconColor: CATEGORY_COLORS.survey.iconColor,
    gradientColor: CATEGORY_COLORS.survey.gradientColor,
  },
  interview: {
    ...NODE_SHARED_STYLES,
    iconColor: CATEGORY_COLORS.interview.iconColor,
    gradientColor: CATEGORY_COLORS.interview.gradientColor,
  },
  insight: {
    ...NODE_SHARED_STYLES,
    iconColor: CATEGORY_COLORS.insight.iconColor,
    gradientColor: CATEGORY_COLORS.insight.gradientColor,
  },
} as const

// Human-readable display names for node categories (singular)
export const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  venture: 'Venture',
  need: 'Unmet need',
  idea: 'Idea',
  information: 'Information',
  summarized: 'Summary',
  storyboard: 'Storyboard',
  survey: 'Survey',
  interview: 'Interview',
  insight: 'Insight',
}

// Plural forms for node categories (handles irregular plurals)
export const CATEGORY_DISPLAY_NAMES_PLURAL: Record<string, string> = {
  venture: 'Ventures',
  need: 'Unmet needs',
  idea: 'Ideas',
  information: 'Information',
  summarized: 'Summaries',
  storyboard: 'Storyboards',
  survey: 'Surveys',
  interview: 'Interviews',
  insight: 'Insights',
}

// React Flow Edge Configuration
export const EDGE_CONFIG = {
  DEFAULT_COLOR: '#9ca3af', // Default grey for unknown categories
  DEFAULT_STROKE_WIDTH: 1.5,
  MIN_STROKE_WIDTH: 1.5,
  MAX_STROKE_WIDTH: 20,
  MARKER_WIDTH: 12,
  MARKER_HEIGHT: 12,
  // Edge thickness scaling based on children count
  THICKNESS_TIERS: [
    { maxChildren: 0, width: 1.5 },
    { maxChildren: 3, width: 1.5, increment: 0.5 }, // 1.5-3px for 0-3 children
    { maxChildren: 10, width: 3, increment: 0.3 }, // 3-5.1px for 3-10 children
    { maxChildren: 25, width: 5.1, increment: 0.2 }, // 5.1-8.1px for 10-25 children
    { maxChildren: 50, width: 8.1, increment: 0.15 }, // 8.1-11.85px for 25-50 children
    { maxChildren: 100, width: 11.85, increment: 0.08 }, // 11.85-15.85px for 50-100 children
    { maxChildren: Infinity, width: 15.85, increment: 0.04, max: 20 }, // 15.85-20px for 100+ children
  ],
} as const

/**
 * Calculate edge stroke width based on target node's children count
 * More central nodes (more children) get thicker edges
 */
export function calculateEdgeStrokeWidth(childrenCount: number): number {
  if (childrenCount === 0) return EDGE_CONFIG.DEFAULT_STROKE_WIDTH

  const { THICKNESS_TIERS } = EDGE_CONFIG

  for (let i = 0; i < THICKNESS_TIERS.length; i++) {
    const tier = THICKNESS_TIERS[i]
    const prevTier = THICKNESS_TIERS[i - 1]

    if (childrenCount <= tier.maxChildren) {
      // Check if tier has increment property
      if (!('increment' in tier)) return tier.width

      // prevTier is always defined here since we skip tier 0 when childrenCount > 0
      const baseWidth = prevTier.width
      const childrenInTier = childrenCount - prevTier.maxChildren
      const calculatedWidth = baseWidth + childrenInTier * tier.increment

      // Check if tier has max property
      return 'max' in tier ? Math.min(calculatedWidth, tier.max) : calculatedWidth
    }
  }

  return EDGE_CONFIG.MAX_STROKE_WIDTH
}

// Feature flags for header controls
export const HEADER_FEATURE_FLAGS = {
  showAddNodeButton: false,
} as const
